import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import logo from './assets/logo.png';
import { useEnhancedAuth } from './contexts/EnhancedAuthContext';
import { determinePostAuthRoute } from './utils/paymentRouting';
import { DocumentProvider } from './contexts/DocumentContext';
import { ManagerProvider } from './contexts/ManagerContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { EducationHubProvider } from 'contexts/EducationHubContext';
import PaymentStatusGuard from './components/common/PaymentStatusGuard';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './components/pages/HomePage';
import Account from './components/pages/Account';
import Subscription from './components/pages/Subscription';
import ManageAgency from './components/pages/ManageAgency';
import DocumentManagement from './components/pages/DocumentManagement';
import ListingManagement from './components/pages/ListingManagement';
import AddListing from './components/items/ListingManagement/AddListing';
import ViewListing from './components/items/ListingManagement/ViewListing';
import EditListing from './components/items/ListingManagement/EditListing';
import { ListingProvider } from './contexts/ListingContext';
import AcceptInvite from './components/pages/LoginRegister/AcceptInvite';
import ResetPassword from './components/pages/LoginRegister/ResetPassword';
import VerifyEmail from './components/pages/LoginRegister/VerifyEmail';
import SignupSuccess from './components/pages/LoginRegister/SignupSuccess';
import ContactUs from './components/pages/ContactUs';
import EducationHub from './components/pages/EducationHub';
import LoginRegister from './components/pages/LoginRegister/LoginRegister';
import UnpaidAgencyFlow from './components/pages/UnpaidAgencyFlow';
import ContactSupport from './components/pages/ContactSupport';
import PaymentRetry from './components/pages/PaymentRetry';
import PaymentSuccess from './components/pages/PaymentSuccess';
import ChoosePlanStep from './components/items/Signup/ChoosePlanStep';
import Sidebar from 'components/common/Sidebar';
import { SidebarProvider, useSidebar } from 'contexts/SidebarContext';
import { EnhancedAuthProvider } from './contexts/EnhancedAuthContext';
import { InvitationProvider } from './contexts/InvitationContext';
import { AccountActionProvider } from './contexts/AccountActionContext';
import TrialBanner from './components/common/TrialBanner';
import Dashboard from './components/pages/Dashboard';
import AdminLogin from './components/pages/AdminLogin';
import AdminDashboard from './components/pages/AdminDashboard';
import AdminDocumentMap from './components/pages/AdminDocumentMap';
import AdminLegislation from './components/pages/AdminLegislation';
import AdminAppUpdates from './components/pages/AdminAppUpdates';
import AdminGuides from './components/pages/AdminGuides';
import AdminResources from './components/pages/AdminResources';
import AdminNews from './components/pages/AdminNews';
import AdminGuard from './components/admin/AdminGuard';
import AdminLayout from './components/admin/AdminLayout';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import ComplianceLogin from './components/pages/ComplianceLogin';
import ComplianceAgencyList from './components/pages/ComplianceAgencyList';
import ComplianceAgencyDetail from './components/pages/ComplianceAgencyDetail';
import ComplianceGuard from './components/compliance/ComplianceGuard';
import { ComplianceProvider } from './contexts/ComplianceContext';
import PrivacyPolicy from 'components/pages/PrivacyPolicy';
import LoadingSpinner from 'components/common/LoadingSpinner';
const AppContent: React.FC = () => {
  const { 
    isLoggedIn, 
    userDetails, 
    userReady, 
    loading,
    handleLogin, 
    handleLogout, 
    handleUserDetailsUpdate 
  } = useEnhancedAuth();
  const { isAdmin } = useAdmin();

  const { isOpen } = useSidebar();
  let contentStyle = 'App-content-no-sidebar';
  if (isOpen === true) {
    contentStyle = 'App-content-sidebar';
  } else if (isOpen === false) {
    contentStyle = 'App-content-sidebar-collapsed';
  } else if (isOpen === null) {
    contentStyle = 'App-content-sidebar-hidden';
  }
  const location = useLocation();
  const needsOnboarding = !!determinePostAuthRoute(userDetails?.agency);
  const isPlatformPortal = isAdmin || location.pathname.startsWith('/admin') || location.pathname.startsWith('/compliance');
  const portalLabel = location.pathname.startsWith('/admin')
    ? 'Admin'
    : location.pathname.startsWith('/compliance')
      ? 'Compliance'
      : '';

  if (loading || (!userReady && sessionStorage.getItem('token'))) {
    return <LoadingSpinner />;
  }

  return (
    <div className="App">      
      <header className="App-header">
        <div className="App-header-left">
          <Link to="/">
            <img src={logo} alt="Realty Genie" className="App-logo" />
          </Link>
          <h1>
            Realty Genie
            {portalLabel && <span className="App-portal-badge">{portalLabel}</span>}
          </h1>
          {userDetails && userDetails.agency && (
            <h3 className="App-agency-name">{userDetails.agency.companyName}</h3>
          )}
        </div>
        {!isPlatformPortal && (
          <nav className="App-nav">
            {!isLoggedIn && <Link to="/">Home</Link>}
            {/* <Link to="/">Home</Link> */}
            {isLoggedIn && !needsOnboarding && <Link to="/Dashboard">Dashboard</Link>}
            {isLoggedIn && !needsOnboarding && <Link to="/documents">Document Management</Link>}
            {isLoggedIn && !needsOnboarding && <Link to="/ListingManagement">Listing Management</Link>}
            {isLoggedIn && !needsOnboarding && <Link to="/education">Education Hub</Link>}
            {!isLoggedIn && <Link to="/ContactUs">Contact Us</Link>}
            {!isLoggedIn && <Link to="/LoginRegister">Login</Link>}
            {isLoggedIn && !needsOnboarding && <Link to="/Account">Account</Link>}
          </nav>
        )}
      </header>

      {/* Trial Banner - Show if user is on trial */}
      {!isPlatformPortal && isLoggedIn && 
       userDetails?.agency?.isOnTrial && 
       (userDetails?.agency?.trialDaysRemaining !== undefined) && (
        <TrialBanner 
          trialDaysRemaining={userDetails.agency.trialDaysRemaining}
          trialEndDate={userDetails.agency.trialEndDate}
        />
      )}
      
      <Sidebar />
      <main className={contentStyle}>
        <Routes>
          <Route path="/" element={
            isAdmin ? <Navigate to="/admin" replace /> :
            (isLoggedIn ? <Navigate to="/Account" replace /> : <HomePage />)
          } />
          <Route path="/Dashboard" element={
            <PaymentStatusGuard>
              <ListingProvider>
                <Dashboard 
                  userDetails={userDetails} 
                  onLogout={handleLogout} 
                  />
              </ListingProvider>
            </PaymentStatusGuard>
          } />
          <Route path="/documents" element={
            <PaymentStatusGuard>
              <DocumentProvider>
                <DocumentManagement userDetails={userDetails} userReady={userReady} onLogout={handleLogout} />
              </DocumentProvider>
            </PaymentStatusGuard>
            } />
          <Route path="/ListingManagement" element={
            <PaymentStatusGuard>
              <ListingProvider>
                <ListingManagement userDetails={userDetails} onLogout={handleLogout} />
              </ListingProvider>
            </PaymentStatusGuard>
          } />
          <Route path="/AddListing" element={
            <PaymentStatusGuard>
              <ListingProvider>
                <AddListing userDetails={userDetails} />
              </ListingProvider>
            </PaymentStatusGuard>
          } />
          <Route path="/ListingViewer/:id" element={
            <PaymentStatusGuard>
              <ListingProvider>
                <ViewListing />
              </ListingProvider>
            </PaymentStatusGuard>
          } />
          <Route path="/EditListing/:id" element={
            <PaymentStatusGuard>
              <ListingProvider>
                <EditListing />
              </ListingProvider>
            </PaymentStatusGuard>
          } />
          <Route path="/education" element={
            <PaymentStatusGuard>
              <EducationHub userDetails={userDetails} userReady={userReady} />
            </PaymentStatusGuard>
          } />
          <Route path="/LoginRegister" element={<LoginRegister onLogin={handleLogin}/>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <AdminGuard>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/admin/document-map" element={
            <AdminGuard>
              <AdminLayout>
                <AdminDocumentMap />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/admin/legislation" element={
            <AdminGuard>
              <AdminLayout>
                <AdminLegislation />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/admin/app-updates" element={
            <AdminGuard>
              <AdminLayout>
                <AdminAppUpdates />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/admin/guides" element={
            <AdminGuard>
              <AdminLayout>
                <AdminGuides />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/admin/resources" element={
            <AdminGuard>
              <AdminLayout>
                <AdminResources />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/admin/news" element={
            <AdminGuard>
              <AdminLayout>
                <AdminNews />
              </AdminLayout>
            </AdminGuard>
          } />
          <Route path="/compliance/login" element={<ComplianceLogin />} />
          <Route path="/compliance" element={
            <ComplianceGuard>
              <AdminLayout>
                <ComplianceAgencyList />
              </AdminLayout>
            </ComplianceGuard>
          } />
          <Route path="/compliance/agencies/:agencyId" element={
            <ComplianceGuard>
              <AdminLayout>
                <ComplianceAgencyDetail />
              </AdminLayout>
            </ComplianceGuard>
          } />
          <Route path="/Account" element={
            <PaymentStatusGuard>
              <Account 
                userDetails={userDetails} 
                onLogout={handleLogout} 
                onUserDetailsUpdate={handleUserDetailsUpdate} 
              />
            </PaymentStatusGuard>
          } />
          <Route path="/Subscription" element={
            <PaymentStatusGuard>
              <Subscription />
            </PaymentStatusGuard>
          } />
          <Route path="/ManageAgency" element={
            <PaymentStatusGuard>
              <ManageAgency />
            </PaymentStatusGuard>
          } />
          <Route path="/AcceptInvite" element={<AcceptInvite onLogin={handleLogin} />} />
          <Route path="/ResetPassword" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/signup-success" element={<SignupSuccess />} />
          <Route path="/ContactUs" element={<ContactUs />} />
          <Route path='/PrivacyPolicy' element={<PrivacyPolicy />} />
          <Route path="/unpaid-agency" element={<UnpaidAgencyFlow />} />
          <Route path="/contact-support" element={<ContactSupport />} />
          <Route path="/payment-retry" element={<PaymentRetry />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/choose-plan" element={<ChoosePlanStep />} />
        </Routes>
      </main>
      <footer className="App-footer">
        &copy; 2025 Realty Genie. All rights reserved.
      </footer>
    </div>
  );
};

// Main App - Enhanced Auth Implementation
const App: React.FC = () => (
  <ErrorBoundary>
    <Router>
      <EnhancedAuthProvider>
        <InvitationProvider>
          <AccountActionProvider>
            <ManagerProvider>
              <DocumentProvider>
                <DashboardProvider>
                  <EducationHubProvider>
                    <SidebarProvider>
                      <AdminProvider>
                        <ComplianceProvider>
                          <AppContent />
                        </ComplianceProvider>
                      </AdminProvider>
                    </SidebarProvider>
                  </EducationHubProvider>
                </DashboardProvider>
              </DocumentProvider>
            </ManagerProvider>
          </AccountActionProvider>
        </InvitationProvider>
      </EnhancedAuthProvider>
    </Router>
  </ErrorBoundary>
);

export default App;
